// Mutations de escrita da Configuração (§4.3) — enable de evento e
// CRUD de agendamentos — com UPDATE OTIMISTA + rollback + toast.
//
// Slices 1 e 2 (writes reais): todas as escritas já batem no
// notification-app real (/api/v1/portal). O otimismo dá resposta
// imediata; no onSettled invalidamos a query de eventos e refazemos —
// o backend é a fonte da verdade (ex.: o agendamento nasce com uuid
// real e template default, então o refetch substitui o item otimista
// de uuid temporário pelo persistido; a associação de template resolve
// nome@versão reais).
import { useTranslation } from "react-i18next"

import { useToast } from "@/hooks/use-toast"
import { useMutation, useQueryClient } from "@/libs"
import {
  deleteNotificationCenterEventScheduler,
  patchNotificationCenterWhiteLabelConfiguration,
  patchNotificationCenterWhiteLabelEvent,
  postNotificationCenterDefaultConfiguration,
  postNotificationCenterEventScheduler,
  putNotificationCenterEventImmediateTemplate,
  putNotificationCenterEventScheduler,
} from "@/services/notification-center"
import type {
  NotificationEventSchedulerType,
  NotificationEventTemplateRefType,
  NotificationOriginTransactionType,
  NotificationSchedulerWritePayloadType,
  NotificationSearchChannelType,
  NotificationWhiteLabelEventsResponseType,
} from "@/types/notification-center"

import { matchesEventRow } from "./event-transaction-type"
import { getWhiteLabelEventsQueryKey } from "./hooks"

// Contexto de rollback compartilhado pelas mutations.
type EventsSnapshotContext = {
  previous?: NotificationWhiteLabelEventsResponseType
}

// Alvo de uma escrita de evento: name + linha (ORDER/SUBSCRIPTION).
// Sem transactionType = todas as linhas do evento (formato antigo).
export type EventRowTarget = {
  eventName: string
  transactionType?: NotificationOriginTransactionType
}

// Aplica uma transformação imutável à(s) linha(s) alvo do cache —
// casa por name + transactionType, para o card de Pedido não alterar
// o de Assinatura do mesmo evento.
const patchEventInCache = (
  cache: NotificationWhiteLabelEventsResponseType,
  { eventName, transactionType }: EventRowTarget,
  patch: (
    event: NotificationWhiteLabelEventsResponseType["events"][number]
  ) => NotificationWhiteLabelEventsResponseType["events"][number]
): NotificationWhiteLabelEventsResponseType => ({
  ...cache,
  events: cache.events.map((event) =>
    matchesEventRow(event, eventName, transactionType)
      ? patch(event)
      : event
  ),
})

// Fábrica dos callbacks otimistas: snapshot no onMutate, rollback +
// toast destrutivo no onError, toast de sucesso no onSuccess.
const useOptimisticEventsWrite = <TVariables>(
  whiteLabelId: string,
  applyOptimistic: (
    cache: NotificationWhiteLabelEventsResponseType,
    variables: TVariables
  ) => NotificationWhiteLabelEventsResponseType,
  mutationFn: (variables: TVariables) => Promise<unknown>
) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const queryKey = getWhiteLabelEventsQueryKey(whiteLabelId)

  return useMutation<
    unknown,
    Error,
    TVariables,
    EventsSnapshotContext
  >({
    mutationFn,
    // Write real: o backend é a fonte da verdade — refaz a query no fim.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey })
      const previous =
        queryClient.getQueryData<NotificationWhiteLabelEventsResponseType>(
          queryKey
        )
      if (previous) {
        queryClient.setQueryData(
          queryKey,
          applyOptimistic(previous, variables)
        )
      }
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
      toast({
        title: t(
          "pages.notificationCenter.configurationsDetailPage.writeError"
        ),
        variant: "destructive",
      })
    },
    onSuccess: () => {
      toast({
        title: t(
          "pages.notificationCenter.configurationsDetailPage.writeSuccess"
        ),
      })
    },
  })
}

// POST …/configurations/default — cria a configuração padrão do WL
// (eventos padrão + templates padrão do sistema). Diferente dos
// writes otimistas acima, aqui o GET de eventos é OUTRO endpoint e o
// resultado não é previsível no cliente → invalida/refaz a query no
// sucesso (toast + refetch), sem otimismo.
export const useCreateDefaultConfiguration = (
  whiteLabelId: string,
  // A lista de Configurações reaproveita esta mutation e precisa
  // refazer a query da lista de WLs no sucesso (o WL sai de "não
  // configurado" para "configurado") — daí o callback opcional.
  options: { onSuccess?: () => void | Promise<void> } = {}
) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  return useMutation<unknown, Error, void>({
    mutationFn: () =>
      postNotificationCenterDefaultConfiguration(
        whiteLabelId
      ),
    onSuccess: async () => {
      toast({
        title: t(
          "pages.notificationCenter.configurationsDetailPage.defaultConfig.success"
        ),
      })
      await queryClient.invalidateQueries({
        queryKey: getWhiteLabelEventsQueryKey(whiteLabelId),
      })
      await options.onSuccess?.()
    },
    onError: () => {
      toast({
        title: t(
          "pages.notificationCenter.configurationsDetailPage.defaultConfig.error"
        ),
        variant: "destructive",
      })
    },
  })
}

// PATCH …/configuration — altera o remetente (mailFrom) do WL com
// UPDATE OTIMISTA na configuração rica (§4.3) + rollback + toast. O
// mailFrom vive no cache de eventos do WL (mesma query key).
export const useUpdateWhiteLabelMailFrom = (
  whiteLabelId: string
) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const queryKey = getWhiteLabelEventsQueryKey(whiteLabelId)

  return useMutation<
    unknown,
    Error,
    { mailFrom: string },
    EventsSnapshotContext
  >({
    mutationFn: ({ mailFrom }) =>
      patchNotificationCenterWhiteLabelConfiguration(
        whiteLabelId,
        { mailFrom }
      ),
    // Slice 1 (write real): confirma o mailFrom persistido no backend.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey }),
    onMutate: async ({ mailFrom }) => {
      await queryClient.cancelQueries({ queryKey })
      const previous =
        queryClient.getQueryData<NotificationWhiteLabelEventsResponseType>(
          queryKey
        )
      if (previous) {
        queryClient.setQueryData(queryKey, {
          ...previous,
          mailFrom,
        })
      }
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
      toast({
        title: t(
          "pages.notificationCenter.configurationsDetailPage.writeError"
        ),
        variant: "destructive",
      })
    },
    onSuccess: () => {
      toast({
        title: t(
          "pages.notificationCenter.configurationsDetailPage.writeSuccess"
        ),
      })
    },
  })
}

// PATCH …/events/:eventName — liga/desliga o evento no WL.
export const useToggleNotificationEvent = (
  whiteLabelId: string
) =>
  useOptimisticEventsWrite<
    EventRowTarget & { enabled: boolean }
  >(
    whiteLabelId,
    (cache, { enabled, ...target }) =>
      patchEventInCache(cache, target, (event) => ({
        ...event,
        enabled,
      })),
    ({ eventName, enabled, transactionType }) =>
      patchNotificationCenterWhiteLabelEvent(
        whiteLabelId,
        eventName,
        { enabled },
        transactionType
      )
  )

// Variables do write de template do envio imediato: canal + uuid do
// contrato + ref resolvida (nome@versão) p/ o cache otimista exibir.
export type ImmediateTemplateWriteVariables =
  EventRowTarget & {
    channel: NotificationSearchChannelType
    templateUuid: string
    optimisticTemplate: NotificationEventTemplateRefType
  }

// PUT …/events/:eventName/templates — associa/troca o template do
// envio imediato de UM canal (otimista: substitui a ref do canal ou
// adiciona quando o canal ainda não tinha template).
export const useUpdateEventImmediateTemplate = (
  whiteLabelId: string
) =>
  useOptimisticEventsWrite<ImmediateTemplateWriteVariables>(
    whiteLabelId,
    (cache, { channel, optimisticTemplate, ...target }) =>
      patchEventInCache(cache, target, (event) => {
        const hasChannel = event.immediate.templates.some(
          (template) => template.channel === channel
        )
        return {
          ...event,
          immediate: {
            templates: hasChannel
              ? event.immediate.templates.map((template) =>
                  template.channel === channel
                    ? optimisticTemplate
                    : template
                )
              : [
                  ...event.immediate.templates,
                  optimisticTemplate,
                ],
          },
        }
      }),
    ({
      eventName,
      transactionType,
      channel,
      templateUuid,
    }) =>
      putNotificationCenterEventImmediateTemplate(
        whiteLabelId,
        eventName,
        { channel, templateUuid },
        transactionType
      )
  )

// Variables comuns dos writes de agendamento: payload do contrato +
// templates resolvidos (nome@versão) p/ o cache otimista exibir.
export type SchedulerWriteVariables = EventRowTarget & {
  payload: NotificationSchedulerWritePayloadType
  optimisticTemplates: NotificationEventTemplateRefType[]
}

// POST …/schedulers — cria agendamento (uuid local temporário até a
// onda 4 devolver o uuid real).
export const useCreateEventScheduler = (
  whiteLabelId: string
) =>
  useOptimisticEventsWrite<SchedulerWriteVariables>(
    whiteLabelId,
    (cache, { payload, optimisticTemplates, ...target }) =>
      patchEventInCache(cache, target, (event) => ({
        ...event,
        schedulers: [
          ...event.schedulers,
          {
            uuid: `SCH_CONF-local-${Date.now()}`,
            direction: payload.direction,
            days: payload.days,
            enabled: payload.enabled,
            templates: optimisticTemplates,
          } satisfies NotificationEventSchedulerType,
        ],
      })),
    ({ eventName, transactionType, payload }) =>
      postNotificationCenterEventScheduler(
        whiteLabelId,
        eventName,
        payload,
        transactionType
      )
  )

// PUT …/schedulers/:uuid — atualiza agendamento (inclui o switch de
// enable do card).
export const useUpdateEventScheduler = (
  whiteLabelId: string
) =>
  useOptimisticEventsWrite<
    SchedulerWriteVariables & { schedulerUuid: string }
  >(
    whiteLabelId,
    (
      cache,
      {
        schedulerUuid,
        payload,
        optimisticTemplates,
        ...target
      }
    ) =>
      patchEventInCache(cache, target, (event) => ({
        ...event,
        schedulers: event.schedulers.map((scheduler) =>
          scheduler.uuid === schedulerUuid
            ? {
                ...scheduler,
                direction: payload.direction,
                days: payload.days,
                enabled: payload.enabled,
                templates: optimisticTemplates,
              }
            : scheduler
        ),
      })),
    ({
      eventName,
      transactionType,
      schedulerUuid,
      payload,
    }) =>
      putNotificationCenterEventScheduler(
        whiteLabelId,
        eventName,
        schedulerUuid,
        payload,
        transactionType
      )
  )

// DELETE …/schedulers/:uuid — remove agendamento.
export const useDeleteEventScheduler = (
  whiteLabelId: string
) =>
  useOptimisticEventsWrite<
    EventRowTarget & { schedulerUuid: string }
  >(
    whiteLabelId,
    (cache, { schedulerUuid, ...target }) =>
      patchEventInCache(cache, target, (event) => ({
        ...event,
        schedulers: event.schedulers.filter(
          (scheduler) => scheduler.uuid !== schedulerUuid
        ),
      })),
    ({ eventName, transactionType, schedulerUuid }) =>
      deleteNotificationCenterEventScheduler(
        whiteLabelId,
        eventName,
        schedulerUuid,
        transactionType
      )
  )
